import {
  DisclosureGrant,
  DisclosureRequest,
  DisclosureRequestsResponse,
  DisclosureGrantsResponse,
} from '../models/disclosure.model';

/** ngx-translate treats dots as nesting; scope ids contain dots (e.g. profile.age_band). */
export function disclosureScopeTranslationKey(scopeId: string): string {
  const suffix = scopeId.trim().replace(/\./g, '_');
  return `app.identity.disclosure.scopes.${suffix}`;
}

export function disclosureScopeFallbackLabel(scopeId: string): string {
  return scopeId.trim() || '—';
}

export function resolveDisclosureScopeLabel(
  scopeId: string,
  translateInstant: (key: string) => string,
): string {
  const trimmed = scopeId?.trim();
  if (!trimmed) {
    return '—';
  }

  const key = disclosureScopeTranslationKey(trimmed);
  const translated = translateInstant(key)?.trim();
  if (translated && translated !== key) {
    return translated;
  }

  return disclosureScopeFallbackLabel(trimmed);
}

export function formatDisclosurePartnerRef(partnerRef: string): string {
  const trimmed = partnerRef?.trim();
  if (!trimmed) {
    return '—';
  }
  return trimmed.replace(/[-_]/g, ' ');
}

/** gRPC int64 may arrive as a plain number or { low, high } over HTTP. */
export function normalizeProtoInt64(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value && typeof value === 'object' && 'low' in value) {
    const low = Number((value as { low: unknown }).low);
    const high = Number((value as { high?: unknown }).high ?? 0);
    if (!Number.isFinite(low)) {
      return 0;
    }
    return high ? high * 4294967296 + low : low;
  }
  return 0;
}

export function formatDisclosureExpiry(expiresAt: unknown): Date | null {
  const unixSeconds = normalizeProtoInt64(expiresAt);
  if (unixSeconds <= 0) {
    return null;
  }
  return new Date(unixSeconds * 1000);
}

export function normalizeDisclosureRequest(
  request: DisclosureRequest,
): DisclosureRequest {
  return {
    ...request,
    expiresAt: normalizeProtoInt64(request.expiresAt),
    createdAt: normalizeProtoInt64(request.createdAt),
    scopeIds: normalizeScopeIds(request.scopeIds),
  };
}

export function normalizeDisclosureRequestsResponse(
  response: DisclosureRequestsResponse,
): DisclosureRequestsResponse {
  return {
    items: (response.items ?? []).map((item) => normalizeDisclosureRequest(item)),
  };
}

export function normalizeDisclosureGrant(
  grant: DisclosureGrant,
): DisclosureGrant {
  return {
    ...grant,
    expiresAt: normalizeProtoInt64(grant.expiresAt),
    grantedAt: normalizeProtoInt64(grant.grantedAt),
    revokedAt: normalizeProtoInt64(grant.revokedAt),
    scopeIds: normalizeScopeIds(grant.scopeIds),
  };
}

export function normalizeDisclosureGrantsResponse(
  response: DisclosureGrantsResponse,
): DisclosureGrantsResponse {
  return {
    items: (response.items ?? []).map((item) => normalizeDisclosureGrant(item)),
  };
}

function normalizeScopeIds(scopeIds: unknown): string[] {
  if (Array.isArray(scopeIds)) {
    return scopeIds
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean);
  }

  if (scopeIds && typeof scopeIds === 'object') {
    return Object.values(scopeIds as Record<string, unknown>)
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean);
  }

  return [];
}
